<script>
  import { assets, base } from '$app/paths';
  import {
    BodyText,
    GraphicBlock,
    FeaturePhoto,
    PhotoPack,
    Video,
  } from '@reuters-graphics/graphics-components';

  import LogBlock from './components/dev/LogBlock.svelte';
  import { containerWidth } from '$utils/propValidators';

  import PostHeadline from '$lib/components/Post/PostHeadline.svelte';

  import slugify from '$utils/slugify';
  import getShortDate from '$utils/getShortDate';

  export let content; // Google doc content for the post

  // Import ai2svelte components...
  import AttackMap from './ai2svelte/attack-map.svelte';
  import TehranMap from './ai2svelte/tehran-map.svelte';
  import IsraelMap from './ai2svelte/israel-map.svelte';
  import AiMap from './ai2svelte/base-map.svelte';
  import Missilefacilities from './ai2svelte/missilefacilities.svelte';
  import Iranmissiles from './ai2svelte/iranmissiles.svelte';
  import Irondome from './ai2svelte/irondome.svelte';
  import Israeldefenses from './ai2svelte/israeldefenses.svelte';

  // POST 3
  import IranNuclearSites from '$lib/ai2svelte/p3_iran-nuclear-sites.svelte';
  import P3BidKaneh from './ai2svelte/p3_bid-kaneh.svelte';
  import P3Kermanshah from './ai2svelte/p3_kermanshah.svelte';
  import P3Isfahan from './ai2svelte/p3_isfahan.svelte';
  import P3Natanz from './ai2svelte/p3_natanz.svelte';
  import P3Shiraz from './ai2svelte/p3_shiraz.svelte';
  import P3Piranshahr from './ai2svelte/p3_piranshahr.svelte';
  import P3IrgcGhadir from './ai2svelte/p3_irgc-ghadir.svelte';
  import P3Fordow from './ai2svelte/p3_fordow.svelte';
  import P3Arak from './ai2svelte/p3_arak.svelte';

  // POST 4
  import USBases from './ai2svelte/p4_us-bases.svelte';
  import P4USMovement from './ai2svelte/p4_us-movement.svelte';
  import P4Fordow from './ai2svelte/p4_fordow.svelte';
  import P4Natanz from './ai2svelte/p4_natanz.svelte';
  import P4Bunkerbuster from './ai2svelte/p4_bunkerbuster.svelte';
  import P4FordowCrosssection from './ai2svelte/p4_fordow_crosssection.svelte';

  // ...and add them to this object.
  const aiCharts = {
    AiMap,
    AttackMap,
    Missilefacilities,
    Iranmissiles,
    Irondome,
    TehranMap,
    Israeldefenses,
    IsraelMap,
    P3BidKaneh,
    P3Kermanshah,
    P3Isfahan,
    P3Natanz,
    P3Shiraz,
    P3Piranshahr,
    P3IrgcGhadir,
    IranNuclearSites,
    P3Fordow,
    P3Arak,
    USBases,
    P4USMovement,
    P4Fordow,
    P4Natanz,
    P4Bunkerbuster,
    P4FordowCrosssection,
  };

  $: publishedDate = getShortDate(content.publishedDate);
</script>

<article id={slugify(content.slugTitle)}>
  <PostHeadline
    hed={content.title}
    slugHed={content.slugTitle}
    authors={content.authors}
    publishTime={content.publishedDate}
    updateTime={content.updatedDate}
  />
  <a href="{base}/{publishedDate}/{slugify(content.slugTitle)}/" hidden>
    {content.title}
  </a>

  <!-- 🔁 Looping through your Google doc blocks... -->
  {#each content.blocks as block}
    <!-- Text block -->
    {#if block.type === 'text'}
      <BodyText class="!fmt-4" text={block.text} />
    {:else if block.type === 'photo'}
      <FeaturePhoto
        src={`${assets}/${block.src}`}
        altText={block.altText}
        caption={block.caption}
        lazy={true}
        width={block.width}
      />
      <!-- Ai2svelte graphic block -->
    {:else if block.type === 'ai-graphic'}
      {#if !aiCharts[block.chart]}
        <LogBlock message={`Unable to find "${block.chart}" in aiCharts`} />
      {:else}
        {@const AiChart = aiCharts[block.chart]}
        <GraphicBlock
          id={block.chart}
          width={containerWidth(block.width)}
          title={block.title}
          description={block.description}
          notes={block.notes}
          ariaDescription={block.altText}
        >
          <AiChart assetsPath={assets || '/'} />
        </GraphicBlock>
      {/if}
    {:else if block.type === 'photo-pack'}
      <!-- Pass `assets` into the image source in graphics kit -->
      <PhotoPack
        id={block.id}
        class={block.class}
        width={block.width}
        textWidth={block.textWidth}
        gap={Number(block.gap)}
        images={block.images.map((img) => ({
          ...img,
          src: `${assets}/${img.src}`,
        }))}
        layouts={[
          {
            breakpoint: 750,
            rows: block.id == 'israel-photopack' ? [2] : [1, 2],
          },
          {
            breakpoint: 450,
            rows: block.id == 'israel-photopack' ? [1, 1] : [1, 2],
          },
        ]}
      />
    {:else if block.type === 'video'}
      <Video
        ariaDescription={block.ariaDescription}
        src={`${assets}/${block.src}`}
        width={block.width}
        loopVideo={true}
        notes={block.caption}
      />
    {:else}
      <LogBlock message={`Unknown block type: "${block.type}"`} />
    {/if}
  {/each}
</article>
